import {
  useMutation,
  useQuery,
  useSuspenseQuery,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAddSnackbarMessage } from '@shared/useAddSnackBar';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import {
  postCreateNewClient,
  getClients,
  getClientById,
  postImportContacts,
  getSearchClients,
  deleteClient,
} from './api';
import { ROUTES } from '@shared/AppRoutes';
import { queryClient } from '../../shared/queries';
import { PartialClient, Client, ClientWithVisitsWithVisitServices } from './entity';
import { useAppNavigate } from '@shared/store';
import { Contact } from '@shared/components/ContactPicker';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';

export const useCreateNewOrUpdateClientMutation = (
  options?: UseMutationOptions<PartialClient, unknown, PartialClient>,
) => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('client.mutation');
  const { trackOnboardingStep } = useAnalytics();

  return useMutation<PartialClient, Error, PartialClient>({
    mutationFn: (clientData: PartialClient) => {
      return postCreateNewClient(clientData);
    },
    onSuccess: (data, variables, context, meta) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['logs'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      queryClient.invalidateQueries({ queryKey: ['client', data.id] });
      queryClient.invalidateQueries({ queryKey: [data.id, 'visits'] });

      trackOnboardingStep('clientCreated', 3);
      addSnackBarMessage({ text: t('upsertSuccess'), type: 'success' });
      options?.onSuccess?.(data, variables, context, meta);
    },
    onError: error => {
      addSnackBarMessage({ text: error.message ?? t('upsertError'), type: 'error' });
    },
  });
};
export const useDeleteClientMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const navigate = useAppNavigate();
  const { t } = useIntlTranslations('client.mutation');

  return useMutation<Client, Error, string>({
    mutationFn: (clientId: string) => deleteClient(clientId),
    onSuccess: client => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['searchClients'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      queryClient.removeQueries({ queryKey: ['client', client.id] });
      addSnackBarMessage({ text: t('deleteSuccess'), type: 'success' });
      navigate(ROUTES.clients.path, { replace: true });
    },
    onError: error => {
      addSnackBarMessage({ text: error.message ?? t('deleteError'), type: 'error' });
    },
  });
};

export const useImportClientMutation = (
  options?: UseMutationOptions<boolean, unknown, { contacts: Contact[] }>,
) => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('client.mutation');
  const { trackEvent, trackOnboardingStep } = useAnalytics();

  return useMutation<boolean, Error, { contacts: Contact[] }>({
    mutationFn: ({ contacts }) => postImportContacts({ contacts }),
    onSuccess: (data: boolean, variables, context, meta) => {
      options?.onSuccess?.(data, variables, context, meta);
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['logs'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      trackEvent('import_clients', {
        clients_count: variables.contacts.length,
      });
      trackOnboardingStep('clientCreated', 3);
      addSnackBarMessage({ text: t('importSuccess'), type: 'success' });
    },
    onError: error => {
      addSnackBarMessage({ text: error.message ?? t('importError'), type: 'error' });
    },
  });
};

export const useSearchClientsQuery = (payload: string[], enabled = true) => {
  return useQuery<ClientWithVisitsWithVisitServices[], Error>({
    queryKey: ['searchClients'],
    queryFn: () => getSearchClients(payload),
    enabled: enabled && !!payload.length,
  });
};

export const useSuspenseClientQuery = (clientId: string) => {
  return useSuspenseQuery<
    ClientWithVisitsWithVisitServices,
    AxiosError<{ error: string; status: number }>
  >({
    queryKey: ['client', clientId],
    queryFn: () => getClientById(clientId),
  });
};

export const useClientsQuery = () => {
  return useQuery<Client[], AxiosError<{ error: string; status: number }>>({
    queryKey: ['clients'],
    queryFn: () => getClients(),
    staleTime: 20 * 1000 * 60,
    refetchOnMount: false,
  });
};
